import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { Box, Divider, IconButton, Tooltip, Typography } from '@mui/material';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter';
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight';
import FormatAlignJustifyIcon from '@mui/icons-material/FormatAlignJustify';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import ViewWeekIcon from '@mui/icons-material/ViewWeek';
import ViewStreamIcon from '@mui/icons-material/ViewStream';
import { isHtmlContent, plainTextToHtml } from '../../utils/richText';

interface RichTextEditorProps {
  value: string;
  onChange: (html: string) => void;
  label?: string;
  placeholder?: string;
  minHeight?: number;
}

const COLUMN_WRAPPER_CLASS = 'rt-columns';

/**
 * Hafif, bağımlılıksız zengin metin editörü.
 * contentEditable + execCommand kullanır; çıktı HTML string olarak saklanır.
 * Paragraf, madde işaretli/numaralı liste, hizalama ve sütun düzeni destekler.
 */
const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  label,
  placeholder,
  minHeight = 140,
}) => {
  const editorRef = useRef<HTMLDivElement | null>(null);
  const isFocusedRef = useRef(false);

  const normalizedIncoming = useMemo(() => {
    if (!value) return '';
    return isHtmlContent(value) ? value : plainTextToHtml(value);
  }, [value]);

  // Dışarıdan gelen değeri, editör odakta değilken DOM'a yansıt.
  useEffect(() => {
    const el = editorRef.current;
    if (!el) return;
    if (isFocusedRef.current) return;
    if (el.innerHTML !== normalizedIncoming) {
      el.innerHTML = normalizedIncoming;
    }
  }, [normalizedIncoming]);

  const emitChange = useCallback(() => {
    const el = editorRef.current;
    if (!el) return;
    onChange(el.innerHTML);
  }, [onChange]);

  const exec = useCallback(
    (command: string, arg?: string) => {
      const el = editorRef.current;
      if (!el) return;
      el.focus();

      // Hizalama komutlarında seçim yoksa / kısmiyse tüm içeriği seç —
      // aksi halde yalnızca imlecin olduğu paragraf hizalanır.
      const isAlignCommand =
        command === 'justifyLeft' ||
        command === 'justifyCenter' ||
        command === 'justifyRight' ||
        command === 'justifyFull';
      if (isAlignCommand) {
        const sel = window.getSelection();
        const hasSelectionInside =
          !!sel &&
          !sel.isCollapsed &&
          !!sel.anchorNode &&
          el.contains(sel.anchorNode) &&
          (!sel.focusNode || el.contains(sel.focusNode));
        if (!hasSelectionInside && sel) {
          const range = document.createRange();
          range.selectNodeContents(el);
          sel.removeAllRanges();
          sel.addRange(range);
        }
      }

      document.execCommand(command, false, arg);
      emitChange();
    },
    [emitChange]
  );

  const setColumns = useCallback(
    (count: number) => {
      const el = editorRef.current;
      if (!el) return;
      el.focus();

      const existing = el.querySelector(`.${COLUMN_WRAPPER_CLASS}`) as HTMLElement | null;

      if (count <= 1) {
        // Sütun düzenini kaldır: içeriği dışarı taşı.
        if (existing) {
          while (existing.firstChild) {
            el.insertBefore(existing.firstChild, existing);
          }
          existing.remove();
        }
      } else if (existing) {
        existing.style.columnCount = String(count);
        existing.style.columnGap = '40px';
      } else {
        const wrapper = document.createElement('div');
        wrapper.className = COLUMN_WRAPPER_CLASS;
        wrapper.style.columnCount = String(count);
        wrapper.style.columnGap = '40px';
        while (el.firstChild) {
          wrapper.appendChild(el.firstChild);
        }
        el.appendChild(wrapper);
      }
      emitChange();
    },
    [emitChange]
  );

  const toolbarButton = (
    title: string,
    icon: React.ReactNode,
    onClick: () => void
  ) => (
    <Tooltip title={title} key={title}>
      <IconButton
        size="small"
        // mousedown yerine onMouseDown ile seçim kaybını engelle
        onMouseDown={(e) => {
          e.preventDefault();
          onClick();
        }}
        sx={{ borderRadius: 1 }}
      >
        {icon}
      </IconButton>
    </Tooltip>
  );

  return (
    <Box>
      {label && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
          {label}
        </Typography>
      )}
      <Box
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          overflow: 'hidden',
          '&:focus-within': { borderColor: 'primary.main', boxShadow: (theme) => `0 0 0 1px ${theme.palette.primary.main}` },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 0.25,
            px: 0.5,
            py: 0.25,
            backgroundColor: 'action.hover',
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          {toolbarButton('Kalın', <FormatBoldIcon fontSize="small" />, () => exec('bold'))}
          {toolbarButton('İtalik', <FormatItalicIcon fontSize="small" />, () => exec('italic'))}
          {toolbarButton('Altı çizili', <FormatUnderlinedIcon fontSize="small" />, () => exec('underline'))}
          <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
          {toolbarButton('Madde işaretli liste', <FormatListBulletedIcon fontSize="small" />, () => exec('insertUnorderedList'))}
          {toolbarButton('Numaralı liste', <FormatListNumberedIcon fontSize="small" />, () => exec('insertOrderedList'))}
          <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
          {toolbarButton('Sola hizala', <FormatAlignLeftIcon fontSize="small" />, () => exec('justifyLeft'))}
          {toolbarButton('Ortala', <FormatAlignCenterIcon fontSize="small" />, () => exec('justifyCenter'))}
          {toolbarButton('Sağa hizala', <FormatAlignRightIcon fontSize="small" />, () => exec('justifyRight'))}
          {toolbarButton('İki yana yasla', <FormatAlignJustifyIcon fontSize="small" />, () => exec('justifyFull'))}
          <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
          {toolbarButton('Tek sütun', <ViewStreamIcon fontSize="small" />, () => setColumns(1))}
          {toolbarButton('İki sütun', <ViewColumnIcon fontSize="small" />, () => setColumns(2))}
          {toolbarButton('Üç sütun', <ViewWeekIcon fontSize="small" />, () => setColumns(3))}
        </Box>

        <Box
          ref={editorRef}
          contentEditable
          suppressContentEditableWarning
          onInput={emitChange}
          onFocus={() => {
            isFocusedRef.current = true;
          }}
          onBlur={() => {
            isFocusedRef.current = false;
            emitChange();
          }}
          data-placeholder={placeholder || ''}
          sx={{
            minHeight,
            px: 1.5,
            py: 1,
            outline: 'none',
            fontSize: '0.95rem',
            lineHeight: 1.6,
            '& p': { margin: '0 0 0.75em' },
            '& ul, & ol': { margin: '0 0 0.75em', paddingLeft: '1.5em' },
            '& .rt-columns': { columnGap: '40px' },
            '&:empty:before': {
              content: 'attr(data-placeholder)',
              color: 'text.disabled',
            },
          }}
        />
      </Box>
    </Box>
  );
};

export default RichTextEditor;
